/**
 * Local storage of emergency answers.
 *
 * While the service is unavailable (API down, database down, network or power outage), the
 * applicant keeps composing: every answer is written here before being sent, and removed once
 * the server acknowledges it. What is left is the replay queue.
 *
 * Answers are encrypted (AES-256-GCM) and signed (HMAC-SHA-256) with two keys derived from the
 * per applicant secret delivered by the API on session access. Both keys are imported as
 * NON EXTRACTABLE CryptoKey: the raw key material never exists in JavaScript again, so the
 * stored answers cannot be read on a shared computer nor re-signed by editing the store.
 *
 * Everything here is failure tolerant: if IndexedDB or WebCrypto is unavailable (private
 * browsing, disabled storage, insecure context), the module reports itself unavailable and no
 * call ever throws, so the exam keeps working with its previous behaviour.
 */

import moment from 'moment';

const DB_NAME = 'managexam-offline';
const DB_VERSION = 1;

export const STORE_ANSWERS = 'answers';
export const STORE_BLOBS = 'blobs';
export const STORE_SNAPSHOTS = 'snapshots';
export const STORE_KEYS = 'keys';

const STORES = [STORE_ANSWERS, STORE_BLOBS, STORE_SNAPSHOTS, STORE_KEYS];

// Kept in sync with server/api/answer/answer.offline.js
const HKDF_SALT = 'managexam-offline-answers-v1';
const HMAC_INFO = 'offline-answers-hmac';
const AES_INFO = 'offline-answers-aes';

const CIPHER_VERSION = 'v1';
const IV_LENGTH = 12;

// A binary answer bigger than this cannot be secured locally: refusing it explicitly is better
// than filling the origin quota and losing the whole queue
export const MAX_BLOB_SIZE = 10 * 1024 * 1024;
export const MIN_FREE_SPACE = 20 * 1024 * 1024;

// Absolute lifetime of any local data, whatever happens to its session
export const ABSOLUTE_TTL_IN_HOURS = 24;

let keys = null;
let backend = null;

/** Encoding helpers */

function encodeText(text) {
    return new TextEncoder().encode(text);
}

function toBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
}

function fromBase64(value) {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for(let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

function toHex(buffer) {
    return Array.from(new Uint8Array(buffer)).map(byte => byte.toString(16).padStart(2, '0')).join('');
}

function getSubtle() {
    return (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) || null;
}

/** IndexedDB backend (replaceable, so the queue logic can be tested without IndexedDB) */

function getIndexedDB() {
    return (typeof window !== 'undefined' && (window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB)) || null;
}

function openDatabase() {
    return new Promise((resolve, reject) => {
        const indexedDB = getIndexedDB();
        if(!indexedDB) {
            reject(new Error('OfflineStorageUnavailable'));
            return;
        }
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            STORES.forEach(store => {
                if(!db.objectStoreNames.contains(store)) {
                    db.createObjectStore(store);
                }
            });
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('OfflineStorageOpenError'));
        request.onblocked = () => reject(new Error('OfflineStorageBlocked'));
    });
}

function createIndexedDBBackend() {
    let databasePromise = null;

    const getDatabase = () => {
        if(!databasePromise) {
            databasePromise = openDatabase().catch(err => {
                databasePromise = null;
                throw err;
            });
        }
        return databasePromise;
    };

    const runTransaction = (store, mode, action) => getDatabase().then(db => new Promise((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = action(transaction.objectStore(store));
        transaction.onabort = () => reject(transaction.error || new Error('OfflineStorageTransactionAborted'));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('OfflineStorageRequestError'));
    }));

    return {
        get: (store, key) => runTransaction(store, 'readonly', objectStore => objectStore.get(key)),
        getAll: store => runTransaction(store, 'readonly', objectStore => objectStore.getAll()),
        put: (store, key, value) => runTransaction(store, 'readwrite', objectStore => objectStore.put(value, key)),
        remove: (store, key) => runTransaction(store, 'readwrite', objectStore => objectStore.delete(key)),
        clear: store => runTransaction(store, 'readwrite', objectStore => objectStore.clear()),
    };
}

function getBackend() {
    if(!backend) {
        backend = createIndexedDBBackend();
    }
    return backend;
}

/*
 * Replace the storage backend. Test purpose only.
 */
export function setStorageBackend(newBackend) {
    backend = newBackend;
    keys = null;
}

/*
 * Can answers be secured on this device? Checked once when entering the exam: a failure here
 * disables the whole feature instead of letting it fail answer by answer.
 */
export function isOfflineStorageAvailable() {
    if(!getSubtle()) {
        return Promise.resolve(false);
    }
    return getBackend().get(STORE_KEYS, '__availability__')
        .then(() => true)
        .catch(err => {
            console.error('OfflineStorage::: unavailable', err);
            return false;
        });
}

/*
 * Ask the browser to make the database non evictable, so the queue survives disk pressure
 */
export function requestPersistentStorage() {
    if(typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.persist) {
        return Promise.resolve(false);
    }
    return navigator.storage.persisted()
        .then(isPersisted => (isPersisted ? true : navigator.storage.persist()))
        .catch(() => false);
}

/*
 * Check a binary answer can be stored: too big, or not enough room left on the origin quota
 *
 * @returns Promise resolving { isStorable, reason }
 */
export function checkBlobStorable(blob) {
    if(!blob || blob.size > MAX_BLOB_SIZE) {
        return Promise.resolve({ isStorable: false, reason: 'size' });
    }
    if(typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
        return Promise.resolve({ isStorable: true, reason: '' });
    }
    return navigator.storage.estimate().then(estimate => {
        const free = (estimate.quota || 0) - (estimate.usage || 0);
        if(estimate.quota && free - blob.size < MIN_FREE_SPACE) {
            return { isStorable: false, reason: 'quota' };
        }
        return { isStorable: true, reason: '' };
    }).catch(() => ({ isStorable: true, reason: '' }));
}

/** Keys */

/*
 * Derive and store the two non extractable keys from the secret delivered by the API
 */
export function initOfflineKeys(secretBase64) {
    const subtle = getSubtle();
    if(!subtle || !secretBase64) {
        return Promise.resolve(false);
    }

    const deriveParams = info => ({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: encodeText(HKDF_SALT),
        info: encodeText(info),
    });

    return subtle.importKey('raw', fromBase64(secretBase64), 'HKDF', false, ['deriveKey'])
        .then(baseKey => Promise.all([
            subtle.deriveKey(deriveParams(HMAC_INFO), baseKey, { name: 'HMAC', hash: 'SHA-256', length: 256 }, false, ['sign']),
            subtle.deriveKey(deriveParams(AES_INFO), baseKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']),
        ]))
        .then(([hmac, aes]) => {
            keys = { hmac, aes };
            // Persisted so a page reload during the outage can still read and replay the queue
            return getBackend().put(STORE_KEYS, 'current', keys).catch(err => {
                console.error('OfflineStorage::: initOfflineKeys: persist', err);
                return null;
            }).then(() => true);
        })
        .catch(err => {
            console.error('OfflineStorage::: initOfflineKeys', err);
            return false;
        });
}

function getKeys() {
    if(keys) {
        return Promise.resolve(keys);
    }
    return getBackend().get(STORE_KEYS, 'current').then(storedKeys => {
        if(storedKeys && storedKeys.hmac && storedKeys.aes) {
            keys = storedKeys;
            return keys;
        }
        return null;
    }).catch(() => null);
}

export function hasOfflineKeys() {
    return getKeys().then(currentKeys => !!currentKeys);
}

/** Cipher and signature */

function getAad(applicantId, questionId) {
    return encodeText(`answer:${applicantId}:${questionId}`);
}

function toBuffer(value) {
    if(typeof value === 'string') {
        return encodeText(value);
    }
    return value;
}

export function encryptValue(value, aad) {
    return getKeys().then(currentKeys => {
        if(!currentKeys) {
            return null;
        }
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
        return getSubtle().encrypt(
            { name: 'AES-GCM', iv, additionalData: aad },
            currentKeys.aes,
            typeof value === 'string' || value instanceof ArrayBuffer || ArrayBuffer.isView(value) ? toBuffer(value) : encodeText(JSON.stringify(value)),
        ).then(ciphertext => {
            const payload = new Uint8Array(iv.length + ciphertext.byteLength);
            payload.set(iv, 0);
            payload.set(new Uint8Array(ciphertext), iv.length);
            return `${CIPHER_VERSION}$${toBase64(payload.buffer)}`;
        });
    });
}

export function decryptValue(stored, aad) {
    return getKeys().then(currentKeys => {
        if(!currentKeys || !stored) {
            return null;
        }
        const [version, payload] = `${stored}`.split('$');
        if(version !== CIPHER_VERSION || !payload) {
            return null;
        }
        const bytes = fromBase64(payload);
        return getSubtle().decrypt(
            { name: 'AES-GCM', iv: bytes.slice(0, IV_LENGTH), additionalData: aad },
            currentKeys.aes,
            bytes.slice(IV_LENGTH),
        );
    }).catch(err => {
        console.error('OfflineStorage::: decryptValue', err);
        return null;
    });
}

/*
 * Deterministic serialization (recursively sorted keys), so the device and the API sign the very
 * same bytes. Kept in sync with stableStringify in server/api/answer/answer.offline.js
 */
export function stableStringify(value) {
    if(value === null || typeof value !== 'object') {
        return JSON.stringify(value === undefined ? null : value);
    }
    if(Array.isArray(value)) {
        return `[${value.map(item => stableStringify(item)).join(',')}]`;
    }
    return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

/*
 * Signed payload of an answer. Kept in sync with buildSignedPayload in answer.offline.js
 */
export function buildSignedPayload(answer) {
    const offline = answer.offline || {};
    return {
        applicant: `${answer.applicant || ''}`,
        question: `${answer.question || ''}`,
        values: answer.values || [],
        reasonning: answer.reasonning || '',
        dates: {
            creation: (answer.dates || {}).creation || '',
            lastSave: (answer.dates || {}).lastSave || '',
        },
        offline: {
            savedAt: offline.savedAt || '',
            sequence: offline.sequence || 0,
            previousHash: offline.previousHash || '',
        },
    };
}

export function signAnswer(answer) {
    return getKeys().then(currentKeys => {
        if(!currentKeys) {
            return '';
        }
        return getSubtle().sign('HMAC', currentKeys.hmac, encodeText(stableStringify(buildSignedPayload(answer)))).then(toHex);
    }).catch(err => {
        console.error('OfflineStorage::: signAnswer', err);
        return '';
    });
}

/** Answers */

function getAnswerKey(applicantId, questionId) {
    return `${applicantId}:${questionId}`;
}

/*
 * Store an answer. Metadata stays readable so the queue can be listed and purged without
 * decrypting anything; the answer content itself is encrypted.
 */
export function putAnswerEntry(answer, expiresAt, hasFailed = false) {
    const aad = getAad(answer.applicant, answer.question);
    return encryptValue(answer, aad).then(payload => {
        if(!payload) {
            return null;
        }
        const entry = {
            applicant: `${answer.applicant}`,
            question: `${answer.question}`,
            sequence: (answer.offline || {}).sequence || 0,
            savedAt: (answer.offline || {}).savedAt || '',
            blobs: (answer.offline || {}).blobs || [],
            // Every answer is written here before being sent, so being stored proves nothing on
            // its own: only an answer that failed to reach the service is worth reporting
            hasFailed,
            expiresAt: expiresAt || moment.utc().add(ABSOLUTE_TTL_IN_HOURS, 'hours').format(),
            payload,
        };
        return getBackend().put(STORE_ANSWERS, getAnswerKey(answer.applicant, answer.question), entry).then(() => entry);
    }).catch(err => {
        console.error('OfflineStorage::: putAnswerEntry', err);
        return null;
    });
}

/*
 * Flag a stored answer as never delivered, so it feeds the applicant banner and the replay
 */
export function markAnswerEntryFailed(applicantId, questionId) {
    const key = getAnswerKey(applicantId, questionId);
    return getBackend().get(STORE_ANSWERS, key).then(entry => {
        if(!entry || entry.hasFailed) {
            return false;
        }
        return getBackend().put(STORE_ANSWERS, key, { ...entry, hasFailed: true }).then(() => true);
    }).catch(err => {
        console.error('OfflineStorage::: markAnswerEntryFailed', err);
        return false;
    });
}

/*
 * Read one stored answer, decrypted
 */
export function getAnswerEntry(applicantId, questionId) {
    return getBackend().get(STORE_ANSWERS, getAnswerKey(applicantId, questionId)).then(entry => {
        if(!entry) {
            return null;
        }
        return decryptValue(entry.payload, getAad(applicantId, questionId)).then(decrypted => {
            if(!decrypted) {
                return null;
            }
            return { ...entry, answer: JSON.parse(new TextDecoder().decode(decrypted)) };
        });
    }).catch(err => {
        console.error('OfflineStorage::: getAnswerEntry', err);
        return null;
    });
}

/*
 * All pending answers of an applicant, oldest save first
 */
export function getAnswerEntries(applicantId) {
    return getBackend().getAll(STORE_ANSWERS).then(entries => {
        return (entries || [])
            .filter(entry => entry && `${entry.applicant}` === `${applicantId}`)
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    }).catch(err => {
        console.error('OfflineStorage::: getAnswerEntries', err);
        return [];
    });
}

export function removeAnswerEntry(applicantId, questionId) {
    return getBackend().remove(STORE_ANSWERS, getAnswerKey(applicantId, questionId)).catch(err => {
        console.error('OfflineStorage::: removeAnswerEntry', err);
        return null;
    });
}

/** Binary answers (file and audio questions) */

export function putBlobEntry(path, blob, applicantId, expiresAt) {
    return checkBlobStorable(blob).then(({ isStorable, reason }) => {
        if(!isStorable) {
            return { isStored: false, reason };
        }
        return blob.arrayBuffer()
            .then(buffer => encryptValue(buffer, encodeText(`blob:${applicantId}:${path}`)))
            .then(payload => {
                if(!payload) {
                    return { isStored: false, reason: 'unavailable' };
                }
                return getBackend().put(STORE_BLOBS, path, {
                    path,
                    applicant: `${applicantId}`,
                    type: blob.type || '',
                    expiresAt: expiresAt || moment.utc().add(ABSOLUTE_TTL_IN_HOURS, 'hours').format(),
                    payload,
                }).then(() => ({ isStored: true, reason: '' }));
            });
    }).catch(err => {
        // A full quota must never break the queue: the text answers keep flowing
        console.error('OfflineStorage::: putBlobEntry', err);
        return { isStored: false, reason: err && err.name === 'QuotaExceededError' ? 'quota' : 'error' };
    });
}

export function getBlobEntry(path) {
    return getBackend().get(STORE_BLOBS, path).then(entry => {
        if(!entry) {
            return null;
        }
        return decryptValue(entry.payload, encodeText(`blob:${entry.applicant}:${entry.path}`)).then(decrypted => {
            if(!decrypted) {
                return null;
            }
            return { ...entry, blob: new Blob([decrypted], { type: entry.type || '' }) };
        });
    }).catch(err => {
        console.error('OfflineStorage::: getBlobEntry', err);
        return null;
    });
}

export function removeBlobEntry(path) {
    return getBackend().remove(STORE_BLOBS, path).catch(err => {
        console.error('OfflineStorage::: removeBlobEntry', err);
        return null;
    });
}

/** Session snapshot, used to boot the exam again while the API is still down */

export function putSnapshot(applicantId, data, expiresAt) {
    return encryptValue(data, encodeText(`snapshot:${applicantId}`)).then(payload => {
        if(!payload) {
            return null;
        }
        return getBackend().put(STORE_SNAPSHOTS, `${applicantId}`, {
            applicant: `${applicantId}`,
            // Session code kept readable, so a page reloaded from its URL alone can find its snapshot
            code: ((data || {}).session || {}).code || '',
            expiresAt: expiresAt || moment.utc().add(ABSOLUTE_TTL_IN_HOURS, 'hours').format(),
            payload,
        });
    }).catch(err => {
        console.error('OfflineStorage::: putSnapshot', err);
        return null;
    });
}

function decryptSnapshotEntry(entry) {
    if(!entry || moment.utc().isAfter(entry.expiresAt)) {
        return Promise.resolve(null);
    }
    return decryptValue(entry.payload, encodeText(`snapshot:${entry.applicant}`)).then(decrypted => {
        return decrypted ? JSON.parse(new TextDecoder().decode(decrypted)) : null;
    });
}

/*
 * Read a snapshot from an applicant id or, when the page was reloaded from its URL only, from
 * the session code
 */
export function getSnapshot(applicantIdOrCode) {
    const reference = `${applicantIdOrCode || ''}`;
    if(!reference) {
        return Promise.resolve(null);
    }
    return getBackend().get(STORE_SNAPSHOTS, reference).then(entry => {
        if(entry) {
            return decryptSnapshotEntry(entry);
        }
        return getBackend().getAll(STORE_SNAPSHOTS).then(entries => {
            const matchingEntry = (entries || []).find(candidate => candidate && candidate.code && candidate.code.toUpperCase() === reference.toUpperCase());
            return matchingEntry ? decryptSnapshotEntry(matchingEntry) : null;
        });
    }).catch(err => {
        console.error('OfflineStorage::: getSnapshot', err);
        return null;
    });
}

/** Cleanup */

/*
 * Remove everything belonging to an applicant. Called once the exam is validated.
 */
export function clearApplicantData(applicantId) {
    const currentBackend = getBackend();
    return Promise.all([
        getAnswerEntries(applicantId).then(entries => Promise.all(entries.map(entry => currentBackend.remove(STORE_ANSWERS, getAnswerKey(entry.applicant, entry.question))))),
        currentBackend.getAll(STORE_BLOBS).then(entries => Promise.all((entries || []).filter(entry => entry && `${entry.applicant}` === `${applicantId}`).map(entry => currentBackend.remove(STORE_BLOBS, entry.path)))),
        currentBackend.remove(STORE_SNAPSHOTS, `${applicantId}`),
    ]).catch(err => {
        console.error('OfflineStorage::: clearApplicantData', err);
        return null;
    });
}

/*
 * Remove every entry whose replay window is over. Answers left on the device are personal data:
 * once they can no longer be sent, they have no reason to stay.
 */
export function purgeExpiredEntries(now = null) {
    const reference = now ? moment.utc(now) : moment.utc();
    const currentBackend = getBackend();
    const purgeStore = (store, getKey) => currentBackend.getAll(store).then(entries => Promise.all(
        (entries || [])
            .filter(entry => !entry || !entry.expiresAt || reference.isAfter(entry.expiresAt))
            .map(entry => currentBackend.remove(store, getKey(entry))),
    ));

    return Promise.all([
        purgeStore(STORE_ANSWERS, entry => (entry ? getAnswerKey(entry.applicant, entry.question) : '')),
        purgeStore(STORE_BLOBS, entry => (entry ? entry.path : '')),
        purgeStore(STORE_SNAPSHOTS, entry => (entry ? `${entry.applicant}` : '')),
    ]).catch(err => {
        console.error('OfflineStorage::: purgeExpiredEntries', err);
        return null;
    });
}
